import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { ChevronLeft, ChevronRight, Clock, Play, Square, Plus } from 'lucide-react';
import { useCreateEntry, useDeleteEntry, useStopTimer, useTimer } from '@/hooks/useEntries';
import { useNameLookup } from '@/hooks/useReference';
import { useSession } from '@/hooks/useSession';
import { addDays, elapsed, moneyCompact, parseDay, today } from '@/lib/format';
import type { TimeEntry } from '@/types';
import { Screen } from '@m/ui/Screen';
import { Card, ColorBar, Group, SectionHeader } from '@m/ui/List';
import { BarButton, Button, Empty, Segmented, SkeletonList } from '@m/ui/Controls';
import { SwipeRow } from '@m/ui/SwipeRow';
import { withUndo } from '@m/ui/undo';
import { cn } from '@m/ui/cn';
import { EntrySheet } from './EntrySheet';
import { mondayOf, oneDecimal, useEntryValue, useWeekEntries, weekDays } from './time/week';

const SHORT_DAY = new Intl.DateTimeFormat('en-US', { weekday: 'short' });
const LONG_DAY = new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
const MONTH_DAY = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
const CLOCK = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' });

type Editing = { entry: TimeEntry | null; mode: 'entry' | 'timer' } | null;

/**
 * The Time tab (S01, S02, S05). Day view: the week strip, the running timer,
 * and the selected day's entries. Week view: the week's total, a bar per day
 * and a row per day that opens it. `?view=week&day=YYYY-MM-DD` in the URL.
 */
export function TimeScreen() {
  const [params, setParams] = useSearchParams();
  const day = params.get('day') ?? today();
  const view = params.get('view') === 'week' ? 'week' : 'day';
  const go = (next: { day?: string; view?: 'day' | 'week' }) => {
    const d = next.day ?? day;
    const v = next.view ?? view;
    const p: Record<string, string> = {};
    if (d !== today()) p.day = d;
    if (v === 'week') p.view = 'week';
    setParams(p, { replace: true });
  };

  const week = useWeekEntries(day);
  const timer = useTimer().data;
  const value = useEntryValue();
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const items = useMemo(() => (week.data?.items ?? []).filter((e) => !hidden.has(e.id)), [week.data, hidden]);
  const byDay = useMemo(() => {
    const m = new Map<string, TimeEntry[]>();
    for (const e of items) m.set(e.date, [...(m.get(e.date) ?? []), e]);
    return m;
  }, [items]);

  const [editing, setEditing] = useState<Editing>(null);
  const days = weekDays(day);

  return (
    <Screen
      title="Time"
      navCenter={view === 'week' ? <WeekNav day={day} onChange={(d) => go({ day: d })} /> : undefined}
      actions={
        view === 'day' ? (
          <>
            <BarButton onClick={() => go({ day: today() })}>Today</BarButton>
            <BarButton aria-label="Log time" onClick={() => setEditing({ entry: null, mode: 'entry' })}>
              <Plus className="size-[26px]" strokeWidth={2.2} />
            </BarButton>
          </>
        ) : undefined
      }
    >
      <div className="-mt-1 flex flex-col gap-3.5">
        <Segmented
          value={view}
          onChange={(v) => go({ view: v })}
          options={[
            { value: 'day', label: 'Day' },
            { value: 'week', label: 'Week' }
          ]}
        />

        {view === 'day' ? (
          <>
            <DayStrip days={days} selected={day} byDay={byDay} onSelect={(d) => go({ day: d })} />
            {timer && <TimerCard />}
            <DayList
              day={day}
              entries={byDay.get(day) ?? []}
              loading={week.isPending}
              value={value}
              onEdit={(entry) => setEditing({ entry, mode: 'entry' })}
              onNew={(mode) => setEditing({ entry: null, mode })}
              hide={(id, on) =>
                setHidden((s) => {
                  const next = new Set(s);
                  if (on) next.add(id);
                  else next.delete(id);
                  return next;
                })
              }
            />
          </>
        ) : (
          <WeekView
            days={days}
            selected={day}
            byDay={byDay}
            loading={week.isPending}
            value={value}
            timerRunning={Boolean(timer)}
            onOpenDay={(d) => go({ day: d, view: 'day' })}
          />
        )}
      </div>
      {/* Room under the last row for the floating button. */}
      <div className="h-16" />


      <EntrySheet
        key={editing ? `${editing.entry?.id ?? 'new'}-${editing.mode}-${day}` : 'closed'}
        open={editing !== null}
        entry={editing?.entry ?? null}
        mode={editing?.mode}
        defaultDate={day}
        onClose={() => setEditing(null)}
      />
    </Screen>
  );
}

function WeekNav({ day, onChange }: { day: string; onChange: (d: string) => void }) {
  const start = mondayOf(day);
  const end = addDays(start, 6);
  const startDate = parseDay(start);
  const endDate = parseDay(end);
  const label =
    startDate.getMonth() === endDate.getMonth()
      ? `${MONTH_DAY.format(startDate)} – ${endDate.getDate()}`
      : `${MONTH_DAY.format(startDate)} – ${MONTH_DAY.format(endDate)}`;
  return (
    <div className="flex w-full items-center justify-between px-2">
      <button type="button" aria-label="Previous week" onClick={() => onChange(addDays(day, -7))} className="flex size-11 items-center justify-center text-[var(--accent)] active:opacity-50">
        <ChevronLeft className="size-[22px]" strokeWidth={2.2} />
      </button>
      <span className="text-secondary font-medium text-[var(--text-muted)]">{label}</span>
      <button type="button" aria-label="Next week" onClick={() => onChange(addDays(day, 7))} className="flex size-11 items-center justify-center text-[var(--accent)] active:opacity-50">
        <ChevronRight className="size-[22px]" strokeWidth={2.2} />
      </button>
    </div>
  );
}

function DayStrip({
  days,
  selected,
  byDay,
  onSelect
}: {
  days: string[];
  selected: string;
  byDay: Map<string, TimeEntry[]>;
  onSelect: (d: string) => void;
}) {
  const now = today();
  return (
    <div className="grid grid-cols-7 gap-1">
      {days.map((d) => {
        const isSelected = d === selected;
        const isToday = d === now;
        const future = d > now;
        const has = (byDay.get(d)?.length ?? 0) > 0;
        return (
          <button
            key={d}
            type="button"
            aria-pressed={isSelected}
            aria-current={isToday ? 'date' : undefined}
            onClick={() => onSelect(d)}
            className={cn(
              'flex h-[58px] flex-col items-center justify-center gap-0.5 rounded-xl',
              isSelected ? 'bg-zinc-900 text-zinc-300 dark:bg-zinc-100 dark:text-zinc-600' : future ? 'text-zinc-400 dark:text-zinc-500' : 'text-[var(--text-muted)] active:bg-[var(--pressed)]'
            )}
          >
            <span className="text-[12px] font-medium">{SHORT_DAY.format(parseDay(d))}</span>
            <span
              className={cn(
                'text-body font-semibold',
                isSelected ? 'text-white dark:text-zinc-900' : isToday ? 'text-[var(--accent)]' : !future && 'text-[var(--text)]'
              )}
            >
              {parseDay(d).getDate()}
            </span>
            <span className={cn('size-[5px] rounded-full', has ? (isSelected ? 'bg-indigo-400 dark:bg-indigo-600' : 'bg-zinc-400 dark:bg-zinc-500') : 'bg-transparent')} />
          </button>
        );
      })}
    </div>
  );
}

function TimerCard() {
  const timer = useTimer().data;
  const stop = useStopTimer();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!timer) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [timer]);
  if (!timer) return null;
  const started = new Date(timer.startedAt);
  const secs = (now - started.getTime()) / 1000;
  return (
    <div className="flex items-center gap-3 rounded-2xl bg-zinc-900 py-3.5 pr-3.5 pl-4 text-white dark:bg-zinc-800">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex items-center gap-1.5 text-caption font-medium text-indigo-300">
          <span className="size-2 rounded-full bg-red-400" />
          Running since {CLOCK.format(started)}
        </span>
        <span className="tnum text-[32px] leading-[38px] font-semibold tracking-[-0.02em]">{elapsed(secs)}</span>
        <span className="truncate text-secondary text-zinc-300">
          {timer.clientName ?? 'Client'} · {timer.activityName ?? 'Activity'}
        </span>
      </div>
      <button
        type="button"
        aria-label="Stop timer"
        onClick={() => stop.mutate()}
        className="flex size-[52px] shrink-0 items-center justify-center rounded-full bg-white text-zinc-900 active:opacity-70"
      >
        <Square className="size-5 fill-current" strokeWidth={0} />
      </button>
    </div>
  );
}

function DayList({
  day,
  entries,
  loading,
  value,
  onEdit,
  onNew,
  hide
}: {
  day: string;
  entries: TimeEntry[];
  loading: boolean;
  value: (e: TimeEntry) => number;
  onEdit: (e: TimeEntry) => void;
  onNew: (mode: 'entry' | 'timer') => void;
  hide: (id: string, on: boolean) => void;
}) {
  const session = useSession();
  const { clientName, activityName } = useNameLookup();
  const del = useDeleteEntry();
  const create = useCreateEntry(session.user);

  if (loading) return <SkeletonList rows={3} bar />;

  if (entries.length === 0) {
    const weekday = new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(parseDay(day));
    return (
      <div className="mt-2">
        <Empty
          icon={<Clock strokeWidth={1.9} />}
          title={day === today() ? 'Nothing logged today' : `Nothing logged on ${weekday}`}
          body="Start a timer when you begin work, or add hours you already did."
        >
          <Button onClick={() => onNew('timer')}>
            <Play className="size-[18px] fill-current" strokeWidth={0} />
            Start timer
          </Button>
          <Button variant="secondary" onClick={() => onNew('entry')}>
            Add hours
          </Button>
        </Empty>
      </div>
    );
  }

  const hours = entries.reduce((s, e) => s + e.time, 0);
  const amount = entries.reduce((s, e) => s + value(e), 0);

  return (
    <section className="flex flex-col gap-2">
      <SectionHeader right={`${oneDecimal(hours)} h · ${moneyCompact(amount)}`}>{LONG_DAY.format(parseDay(day))}</SectionHeader>
      <div className="group overflow-hidden rounded-2xl bg-[var(--surface)]" style={{ ['--inset' as string]: '32px' }}>
        {entries.map((e) => (
          <SwipeRow
            key={e.id}
            title={clientName(e.clientId)}
            leading={{
              label: 'Duplicate',
              tone: 'accent',
              onSelect: () => create.mutate({ date: day, clientId: e.clientId, activityId: e.activityId, time: e.time, misc: e.misc })
            }}
            trailing={[
              {
                label: 'Delete',
                tone: 'danger',
                onSelect: () =>
                  withUndo({
                    message: 'Entry deleted',
                    hide: () => hide(e.id, true),
                    restore: () => hide(e.id, false),
                    commit: () => del.mutate({ id: e.id })
                  })
              }
            ]}
          >
            <button type="button" onClick={() => onEdit(e)} className="flex min-h-16 w-full items-center gap-3 px-4 text-left active:bg-[var(--pressed)]">
              <ColorBar id={e.clientId} />
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="truncate text-body">{clientName(e.clientId)}</span>
                <span className="truncate text-secondary text-[var(--text-muted)]">
                  {[activityName(e.activityId), e.misc].filter(Boolean).join(' · ')}
                </span>
              </span>
              <span className="tnum text-body font-medium">{oneDecimal(e.time)}</span>
            </button>
          </SwipeRow>
        ))}
      </div>
    </section>
  );
}

function WeekView({
  days,
  selected,
  byDay,
  loading,
  value,
  timerRunning,
  onOpenDay
}: {
  days: string[];
  selected: string;
  byDay: Map<string, TimeEntry[]>;
  loading: boolean;
  value: (e: TimeEntry) => number;
  timerRunning: boolean;
  onOpenDay: (d: string) => void;
}) {
  const now = today();
  const totals = days.map((d) => (byDay.get(d) ?? []).reduce((s, e) => s + e.time, 0));
  const hours = totals.reduce((s, h) => s + h, 0);
  const amount = days.flatMap((d) => byDay.get(d) ?? []).reduce((s, e) => s + value(e), 0);
  const max = Math.max(...totals, 1);
  const thisWeek = days.includes(now);
  // Days still to come this week have nothing to list.
  const listed = days.filter((d) => d <= now || (byDay.get(d)?.length ?? 0) > 0);

  if (loading) return <SkeletonList rows={4} />;

  return (
    <>
      <Card className="flex flex-col gap-3.5">
        <div className="flex items-end justify-between">
          <div className="flex flex-col gap-0.5">
            <span className="text-caption font-medium text-[var(--text-muted)]">{thisWeek ? 'This week' : 'Week'}</span>
            <span className="tnum text-[32px] leading-[38px] font-semibold tracking-[-0.02em]">{oneDecimal(hours)} h</span>
          </div>
          <div className="flex flex-col items-end gap-0.5">
            <span className="text-caption font-medium text-[var(--text-muted)]">Billable value</span>
            <span className="tnum text-[20px] leading-7 font-semibold">{moneyCompact(amount)}</span>
          </div>
        </div>
        <div className="grid h-24 grid-cols-7 items-end gap-2">
          {days.map((d, i) => {
            const h = totals[i]!;
            const strong = d === selected || (d === now && !days.includes(selected));
            return (
              <button key={d} type="button" onClick={() => onOpenDay(d)} className="flex h-full flex-col items-center justify-end gap-1.5">
                {h > 0 ? (
                  <span
                    className={cn('w-full rounded-md', strong ? 'bg-[var(--accent)]' : 'bg-indigo-200 dark:bg-indigo-900')}
                    style={{ height: Math.max(6, (h / max) * 72) }}
                  />
                ) : (
                  <span className="h-[3px] w-full rounded-sm bg-[var(--line)]" />
                )}
                <span
                  className={cn(
                    'text-[12px]',
                    strong ? 'font-semibold text-[var(--text)]' : h > 0 ? 'text-[var(--text-muted)]' : 'text-[var(--text-subtle)]'
                  )}
                >
                  {SHORT_DAY.format(parseDay(d))[0]}
                </span>
              </button>
            );
          })}
        </div>
      </Card>

      <Group>
        {listed.map((d) => {
          const i = days.indexOf(d);
          const isToday = d === now;
          return (
            <button
              key={d}
              type="button"
              onClick={() => onOpenDay(d)}
              className={cn('flex h-14 w-full items-center gap-3 px-4 text-left text-body active:bg-[var(--pressed)]', isToday && 'font-semibold')}
            >
              <span className="flex-1">{isToday ? 'Today' : LONG_DAY.format(parseDay(d))}</span>
              <span className={cn('tnum', isToday ? 'text-[var(--accent)]' : 'text-[var(--text-muted)]')}>
                {oneDecimal(totals[i]!)} h{isToday && timerRunning ? ' + timer' : ''}
              </span>
              <ChevronRight className="size-4 text-[var(--text-subtle)]" strokeWidth={2.4} />
            </button>
          );
        })}
      </Group>
    </>
  );
}
